import { LoadMore } from 'components/atoms/LoadMore';
import React, { useCallback, useEffect, useRef } from 'react';
import { DataTable, Checkbox } from '@getro/rombo';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Building2, Contact, Globe, KeyRound, Lock } from 'lucide-react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Link } from 'react-router-dom';

import { SkeletonRow } from '../../components/atoms/rowSkeleton';
import { SharedListsActions } from '../../components/molecules/sharedListsActions';

const HeaderSelectAllCheckbox = ({ allLoadedSelected, someLoadedSelected, onToggle }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = someLoadedSelected;
  }, [someLoadedSelected]);

  return (
    // Full-cell click target. The column.sx above neutralises the header cell's
    // 8px horizontal padding so this 48x40 area aligns with the row cells.
    <Flex
      justifyContent="center"
      alignItems="center"
      sx={{ width: '100%', height: '40px', cursor: 'pointer' }}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <Checkbox
        ref={ref}
        containerProps={{ width: '16px', minWidth: '16px' }}
        sx={{ label: { p: '0px' } }}
        name="lists-select-all"
        aria-label="Select all loaded lists"
        checked={allLoadedSelected}
        readOnly
      />
    </Flex>
  );
};

HeaderSelectAllCheckbox.propTypes = {
  allLoadedSelected: PropTypes.bool.isRequired,
  someLoadedSelected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export const getCompaniesColumns = ({ headerCheckbox } = {}) => [
  {
    Header: headerCheckbox || '',
    accessor: 'select',
    width: '48px',
    disableSortBy: true,
    // rombo wraps Header in <Text> (inline) and applies px:8 + py:8 to <Flex variant="th">.
    // The padding shifts the header checkbox right of the row cells (variant td has
    // only padding-right:8) and the inline <Text> collapses our Flex. Use `&&` for
    // double-specificity to beat the th variant's padding, and stretch the inner
    // <Text> so the 48x40 click target fills the column.
    sx: {
      '&&': { p: 0 },
      '& > *': { width: '100%' },
    },
  },
  {
    Header: 'List',
    accessor: 'name',
    width: '40%',
    disableSortBy: true,
  },
  {
    Header: 'Contacts',
    accessor: 'contacts',
    width: '120px',
    disableSortBy: true,
  },
  {
    Header: 'Location',
    accessor: 'location',
    width: '30%',
    disableSortBy: true,
  },
  {
    Header: 'Visibility',
    accessor: 'visibility',
    width: '160px',
    disableSortBy: true,
  },
  {
    Header: 'Created by',
    accessor: 'createdBy',
    width: '240px',
    disableSortBy: true,
  },
  {
    Header: 'Created on',
    accessor: 'createdOn',
    width: '170px',
    disableSortBy: false,
    sx: { pointerEvents: 'none' },
  },
  {
    Header: '',
    accessor: 'actions',
    width: '64px',
    disableSortBy: true,
  },
];

export const ListDataTable = ({
  sharedLists,
  isLoading,
  isFetching,
  pathname,
  isManager,
  loadMore,
  hasMore,
  queryParams,
  selection,
}) => {
  const showSkeleton = isLoading && !isFetching;
  const { selectedSet, allLoadedSelected, someLoadedSelected, toggleAll, toggleOne } = selection;

  // Pass Header as a function so the column object stays JSON-serialisable
  // (rombo DataTable uses JSON.stringify(cell.column) to build row keys).
  const renderHeaderCheckbox = () => (
    <HeaderSelectAllCheckbox
      allLoadedSelected={allLoadedSelected}
      someLoadedSelected={someLoadedSelected}
      onToggle={toggleAll}
    />
  );

  const companiesColumns = getCompaniesColumns({ headerCheckbox: renderHeaderCheckbox });

  const createListRows = useCallback(
    () =>
      sharedLists.map((list) => {
        let shareIcon = Lock;
        let shareText = 'Internal';

        if (list.passwordProtected) {
          shareIcon = KeyRound;
          shareText = 'Shared';
        } else if (list.shareable) {
          shareIcon = Globe;
          shareText = 'Shared';
        }

        return {
          passwordProtected: list.passwordProtected,
          id: list.id,
          slug: list.slug,
          select: (
            // Full-cell click target. The td variant has padding-right:8 with
            // no left padding, so the cell's content box is 8px narrower than
            // the cell. width: calc(100% + 8px) extends the Flex through that
            // padding so centering happens on the FULL cell width — matching
            // the (zero-padded) header cell. mr: -8px keeps layout flow tidy.
            <Flex
              justifyContent="center"
              alignItems="center"
              sx={{ width: 'calc(100% + 8px)', height: '40px', mr: '-8px', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleOne(list.id);
              }}
            >
              <Checkbox
                containerProps={{ width: '16px', minWidth: '16px' }}
                sx={{ label: { p: '0px' } }}
                name={`lists-select-${list.id}`}
                aria-label={`Select ${list.name}`}
                checked={selectedSet.has(list.id)}
                readOnly
              />
            </Flex>
          ),
          name: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px" color="text.dark" fontWeight={500} p="8px">
              {showSkeleton ? (
                <Flex sx={{ gap: '8px' }}>
                  <SkeletonRow width={16} />
                  <SkeletonRow width={160} />
                </Flex>
              ) : (
                <>
                  <Box
                    as={list.kind === 'people' ? Contact : Building2}
                    height="16px"
                    width="16px"
                    aria-hidden="true"
                    mr="8px"
                    sx={{ color: 'neutral.400', flexShrink: 0 }}
                  />
                  <Box
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {list.name}
                  </Box>
                </>
              )}
            </Flex>
          ),
          contacts: (
            <Flex width="100%" justifyContent="flex-end" fontSize="14px">
              {showSkeleton ? <SkeletonRow width={24} /> : list.contactsCount}
            </Flex>
          ),
          location: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px">
              {showSkeleton ? <SkeletonRow width={100} /> : <Text fontSize="14px">{list.folderPath || '/'}</Text>}
            </Flex>
          ),
          visibility: (
            <Flex width="100%" justifyContent="flex-start" sx={{ gap: '8px', alignItems: 'center' }}>
              {showSkeleton ? (
                <SkeletonRow width={80} />
              ) : (
                <>
                  <Box size="16px" as={shareIcon} sx={{ color: 'neutral.400', flexShrink: 0 }} />
                  <Box as="p" color="text.main" fontSize="14px">
                    {shareText}
                  </Box>
                </>
              )}
            </Flex>
          ),
          createdBy: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px">
              {showSkeleton ? <SkeletonRow width={80} /> : list.pointOfContact.fullName}
            </Flex>
          ),
          createdOn: (
            <Flex width="100%" justifyContent="flex-start" fontSize="14px" color="text.main">
              {showSkeleton ? <SkeletonRow width={160} /> : moment(list.createdAt).format('D MMM YYYY')}
            </Flex>
          ),

          actions: (
            <SharedListsActions
              list={list}
              isManager={isManager}
              triggerStyle={{ justifyContent: 'flex-end' }}
              showDuplicateList={list.kind !== 'companies'}
              queryParams={queryParams}
            />
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isManager, sharedLists, showSkeleton, queryParams, selectedSet],
  );

  return (
    <Flex flexDirection="column" flexGrow="1">
      <DataTable
        sx={{
          pb: 3,
          minWidth: '980px',
          height: ['600px', '500px', '100%'],
          width: ['1000px!important', '1000px!important', '100%!important'],
          flexGrow: 1,
        }}
        canSort
        initialSortBy={{ id: 'createdOn', desc: false }}
        virtuosoProps={{
          useWindowScroll: true,
          components: {
            // eslint-disable-next-line react/no-multi-comp
            Footer: () => (isFetching ? <LoadMore /> : null),
          },
        }}
        loadMore={loadMore}
        items={createListRows()}
        columnDefinition={companiesColumns}
        hasMore={hasMore}
        isLoadingMore={isFetching}
        emptyView={
          <Text pt="40px" sx={{ alignSelf: 'center' }}>
            No results for this search
          </Text>
        }
        isInitialized
        rowProps={({ original }) => ({
          as: Link,
          to: `${pathname}/${original.id}/contacts`,
          sx: {
            textDecoration: 'none!important',
            minHeight: '40px',
            borderBottom: '1px solid',
            borderBottomColor: 'border.subtle',
            transition: 'background-color ease 0.3s',
            py: '8px',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'neutral.20',
            },

            '& > div': {
              '& > div': {
                minHeight: '40px',
                py: '0px',

                '& > div': {
                  minHeight: '40px',
                  margin: 0,
                },
              },

              '& > div > div': {
                display: 'flex',
                alignItems: 'center',
              },
            },
          },
        })}
      />
    </Flex>
  );
};

const noopSelection = {
  selectedSet: new Set(),
  allLoadedSelected: false,
  someLoadedSelected: false,
  toggleAll: () => {},
  toggleOne: () => {},
};

ListDataTable.propTypes = {
  sharedLists: PropTypes.arrayOf(PropTypes.object),
  isFetching: PropTypes.bool,
  isLoading: PropTypes.bool,
  pathname: PropTypes.string,
  isManager: PropTypes.bool,
  loadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  queryParams: PropTypes.object,
  selection: PropTypes.shape({
    selectedSet: PropTypes.instanceOf(Set).isRequired,
    allLoadedSelected: PropTypes.bool.isRequired,
    someLoadedSelected: PropTypes.bool.isRequired,
    toggleAll: PropTypes.func.isRequired,
    toggleOne: PropTypes.func.isRequired,
  }),
};

ListDataTable.defaultProps = {
  sharedLists: [],
  isFetching: false,
  isLoading: false,
  pathname: '',
  isManager: false,
  loadMore: () => {},
  hasMore: false,
  queryParams: undefined,
  selection: noopSelection,
};
